import type { SignInOutput } from "aws-amplify/auth"
import { confirmSignIn, signOut } from "aws-amplify/auth"
import { QRCodeSVG } from "qrcode.react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Alert, AlertDescription } from "./ui/alert"
import { Button } from "./ui/button"
import { InputOTP, InputOTPGroup, InputOTPSlot, REGEXP_ONLY_DIGITS } from "./ui/input-otp"

type NextStep = SignInOutput["nextStep"]

interface MfaChallengeProps {
  /** The nextStep returned by the first confirmSignIn (post email-OTP). */
  initialStep: NextStep
  /** Account name used when rendering a TOTP setup QR (defensive path only). */
  email: string
  /** Called once sign-in reaches the DONE step. */
  onSignedIn: () => void
  /** Called when the user abandons the MFA challenge. */
  onBack: () => void
}

const TOTP_CODE_LENGTH = 6
const SMS_CODE_LENGTH = 6

/**
 * Drives the second-factor portion of sign-in after the passwordless
 * email-OTP first factor has succeeded. Cognito carries the MFA challenge in
 * the same confirmSignIn nextStep, so this component owns the remaining state
 * machine until signInStep === "DONE".
 */
export function MfaChallenge({ initialStep, email, onSignedIn, onBack }: MfaChallengeProps) {
  const { t } = useTranslation()
  const [nextStep, setNextStep] = useState<NextStep>(initialStep)
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  function advance(step: NextStep) {
    if (step.signInStep === "DONE") {
      onSignedIn()
      return
    }
    setCode("")
    setError(null)
    setNextStep(step)
  }

  async function submitChallengeResponse(challengeResponse: string) {
    setIsLoading(true)
    setError(null)
    try {
      const { nextStep: step } = await confirmSignIn({ challengeResponse })
      advance(step)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("components.mfaChallenge.invalidCode"))
      setCode("")
    } finally {
      setIsLoading(false)
    }
  }

  async function handleBack() {
    // Clear the partial (first-factor-only) session so a fresh sign-in starts.
    try {
      await signOut()
    } catch {
      // Ignore — there may be no session to clear.
    }
    onBack()
  }

  function handleCodeChange(value: string, length: number) {
    setCode(value)
    setError(null)
    if (value.length === length) {
      submitChallengeResponse(value)
    }
  }

  function renderCodeInput(length: number) {
    return (
      <div className="space-y-4">
        <div className="flex justify-center">
          <InputOTP
            maxLength={length}
            pattern={REGEXP_ONLY_DIGITS}
            value={code}
            autoFocus
            onChange={(value) => handleCodeChange(value, length)}
            disabled={isLoading}
          >
            <InputOTPGroup>
              {CODE_SLOT_IDS.slice(0, length).map((id, i) => (
                <InputOTPSlot key={id} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        <Button
          onClick={() => submitChallengeResponse(code)}
          className="w-full"
          disabled={isLoading || code.length !== length}
        >
          {isLoading
            ? t("components.mfaChallenge.verifyingText")
            : t("components.mfaChallenge.verifyButton")}
        </Button>
      </div>
    )
  }

  let title: string
  let description: string
  let body: React.ReactNode

  switch (nextStep.signInStep) {
    case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION": {
      title = t("components.mfaChallenge.selectTitle")
      description = t("components.mfaChallenge.selectInstruction")
      const allowed = nextStep.allowedMFATypes ?? ["TOTP"]
      body = (
        <div className="space-y-3">
          {allowed.map((mfaType) => (
            <Button
              key={mfaType}
              className="w-full"
              variant="outline"
              disabled={isLoading}
              onClick={() => submitChallengeResponse(mfaType)}
            >
              {t(`components.mfaChallenge.method.${mfaType}`)}
            </Button>
          ))}
        </div>
      )
      break
    }
    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE": {
      title = t("components.mfaChallenge.totpTitle")
      description = t("components.mfaChallenge.totpInstruction")
      body = renderCodeInput(TOTP_CODE_LENGTH)
      break
    }
    case "CONFIRM_SIGN_IN_WITH_SMS_CODE": {
      const destination = nextStep.codeDeliveryDetails?.destination
      title = t("components.mfaChallenge.smsTitle")
      description = destination
        ? t("components.mfaChallenge.smsInstructionTo", { destination })
        : t("components.mfaChallenge.smsInstruction")
      body = renderCodeInput(SMS_CODE_LENGTH)
      break
    }
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
      // Should not occur under OPTIONAL MFA — handled defensively in case the
      // pool is ever switched to REQUIRED.
      title = t("components.mfaChallenge.setupTitle")
      description = t("components.mfaChallenge.setupInstruction")
      const uri = nextStep.totpSetupDetails.getSetupUri("Legacy Keeper", email).toString()
      body = (
        <div className="space-y-4">
          <div className="flex justify-center">
            <QRCodeSVG value={uri} size={180} />
          </div>
          {renderCodeInput(TOTP_CODE_LENGTH)}
        </div>
      )
      break
    }
    default: {
      title = t("components.mfaChallenge.unexpectedTitle")
      description = t("components.mfaChallenge.unexpectedInstruction")
      body = null
    }
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="text-center">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground mt-2">{description}</p>
      </div>

      {body}

      <Button
        variant="outline"
        onClick={handleBack}
        className="w-full"
        disabled={isLoading}
        type="button"
      >
        {t("components.mfaChallenge.backToSignIn")}
      </Button>
    </div>
  )
}

// Stable keys for the fixed-length code input slots.
const CODE_SLOT_IDS = Array.from({ length: 8 }, (_, i) => `mfa-code-slot-${i}`)
